import { useMemo, useState } from 'react'

import Loader from '@/components/Atoms/Loader/Loader'
import type { CategoryInterface } from '@/types'
import { getStatusBadge } from '@/utils/catalog/approvalStatus'

import cn from '../CreationDictionaryTables.module.scss'

type CreationCategoriesTableProps = {
  data: CategoryInterface[]
  loading: boolean
}

type CategoryRow = {
  id: string
  category: CategoryInterface['category']
  depth: number
  hasChildren: boolean
  ancestorIds: string[]
}

const buildVisibleCategoryRows = (
  nodes: CategoryInterface[],
  expandedIds: Set<string>,
  searchValue: string,
  ancestorIds: string[] = [],
  depth = 0
): CategoryRow[] =>
  [...nodes]
    .sort((a, b) =>
      a.category.description.localeCompare(b.category.description, 'ru')
    )
    .flatMap((node) => {
      const hasChildren = (node.childCategories || []).length > 0
      const row: CategoryRow = {
        id: node.category.id,
        category: node.category,
        depth,
        hasChildren,
        ancestorIds,
      }

      const shouldShowChildren =
        searchValue.trim() !== '' || expandedIds.has(node.category.id)

      return shouldShowChildren
        ? [
            row,
            ...buildVisibleCategoryRows(
              node.childCategories || [],
              expandedIds,
              searchValue,
              [...ancestorIds, node.category.id],
              depth + 1
            ),
          ]
        : [row]
    })

const filterCategoryTree = (
  nodes: CategoryInterface[],
  searchValue: string
): CategoryInterface[] => {
  const normalizedSearch = searchValue.trim().toLowerCase()
  if (!normalizedSearch) return nodes

  return nodes.reduce<CategoryInterface[]>((accumulator, node) => {
    const childCategories = filterCategoryTree(
      node.childCategories || [],
      normalizedSearch
    )
    const isSelfMatched = [
      String(node.category.code),
      node.category.description,
      node.category.status ?? 'APPROVED',
    ].some((value) => value.toLowerCase().includes(normalizedSearch))

    if (isSelfMatched || childCategories.length > 0) {
      accumulator.push({
        ...node,
        childCategories,
      })
    }

    return accumulator
  }, [])
}

function CreationCategoriesTable(props: CreationCategoriesTableProps) {
  const { data, loading } = props
  const [searchValue, setSearchValue] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const filteredTree = useMemo(
    () => filterCategoryTree(data, searchValue),
    [data, searchValue]
  )

  const visibleRows = useMemo(
    () => buildVisibleCategoryRows(filteredTree, expandedIds, searchValue),
    [expandedIds, filteredTree, searchValue]
  )

  const toggleExpanded = (categoryId: string) => {
    setExpandedIds((prevState) => {
      const nextState = new Set(prevState)
      if (nextState.has(categoryId)) nextState.delete(categoryId)
      else nextState.add(categoryId)
      return nextState
    })
  }

  return (
    <div className={cn.block}>
      <div className={cn.toolbar}>
        <div className={cn.toolbarTitle}>Существующие категории</div>
        <input
          value={searchValue}
          className={cn.searchInput}
          placeholder="Поиск по коду или названию..."
          onChange={(event) => setSearchValue(event.target.value)}
        />
      </div>

      <div className={cn.tableViewport}>
        <table className={cn.table}>
          <thead>
            <tr>
              <th className={cn.codeCol}>Код</th>
              <th className={cn.categoryCol}>Категория</th>
              <th className={cn.statusCol}>Статус</th>
            </tr>
          </thead>
          <tbody>
            {loading && visibleRows.length === 0 ? (
              <tr>
                <td className={cn.loadingCell} colSpan={3}>
                  <Loader size={18} />
                </td>
              </tr>
            ) : visibleRows.length > 0 ? (
              visibleRows.map(
                ({ id, category, depth, hasChildren, ancestorIds }) => {
                  const isExpandedRow =
                    searchValue.trim() === '' && expandedIds.has(id)
                  const isExpandedDescendant =
                    searchValue.trim() === '' &&
                    ancestorIds.some((ancestorId) =>
                      expandedIds.has(ancestorId)
                    )

                  return (
                    <tr
                      key={category.id}
                      className={[
                        isExpandedRow ? cn.categoryRowExpanded : '',
                        isExpandedDescendant ? cn.categoryRowDescendant : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <td>{category.code}</td>
                      <td className={cn.nameCell}>
                        <div
                          className={cn.categoryNameWrap}
                          style={{ '--tree-depth': depth }}
                        >
                          {hasChildren ? (
                            <button
                              type="button"
                              className={cn.expandButton}
                              onClick={() => toggleExpanded(id)}
                            >
                              {searchValue.trim() !== '' || expandedIds.has(id)
                                ? '▾'
                                : '▸'}
                            </button>
                          ) : (
                            <span className={cn.expandButtonPlaceholder} />
                          )}
                          <span className={cn.categoryName}>
                            {category.description}
                          </span>
                        </div>
                      </td>
                      <td>{getStatusBadge(category.status) ?? 'Утверждена'}</td>
                    </tr>
                  )
                }
              )
            ) : (
              <tr>
                <td className={cn.emptyCell} colSpan={3}>
                  Категории не найдены
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default CreationCategoriesTable
